import { default as featuresModule, initializeFeatures } from './features';
import { default as localesModule, initializeLocales } from './locales';
import { default as accountModule, initializeAccount } from './account';
import { default as videoModule } from './video';
import { initializeGtm } from './gtm';
import { initializeMTM } from './mtm';
import { default as breadcrumbModule, initializeBreadcrumbs } from './breadcrumb';
import { default as localizationTokenModule, initializeLocalizations } from './localizationToken';
import { default as countryModule, initializeCountry } from './country';
import { default as regionModule, initializeRegion } from './region';
import { default as contextModule, initializeContextData, observeAndDecorateResourceLinks } from './context';
import { default as cartModule, initializeCart } from './cart';
import { default as cartAnalyticsModule } from './cartAnalytics';
import { default as numericStepperModule } from './numericStepper';
import { default as relatedResourcesModule } from './relatedResources';
import { default as dropDownModule } from './dropDown';
import { default as addedToCartModule } from './addedToCartModal';
import { default as dynamicKitModule } from './dynamicKitSlideout';
import { default as baseKitModule } from './baseKitSlideout';
import { default as subscriptionTermsConflictModule } from './subscriptionTermsConflictModal';
import { default as volumeBasedPricingModelModule } from './volumeBasedPricingModel';
import { default as chatModule, initializeChat } from './chat';
import { default as taxonomyModule } from './taxonomy';
import { default as imgixModule } from './imgix';
import { default as NavigationServiceModule } from './NavigationService';
import { default as trialServiceModule } from './trialService';
import { default as experimentModule, initializeExperiment } from './experiment';

import { createHrefListener } from '../../universal-components/src/utils';
import { useLocalStorage } from '../../universal-components/src/Checkout/hooks/useLocalStorage';
import { CHECKOUT_LOCAL_DATA_KEY } from '../../universal-components/src/constants';
import { emit, events, subscribe } from './eventManager';
import { getDecodedQuerystringValue } from './queryUtils';
import logger from './logger';

// Not exported: read it through getDidInit(), so callers cannot rely on a mutable binding.
let didInit = false;
export let didInitFeatures = false;
export let didInitLocales = false;
export let didInitAccount = false;
export let didInitGTM = false;
export let didInitMTM = false;
export let didInitBreadcrumbs = true;
export let didInitCountry = false;
export let didInitCart = false;
export let didInitLocalizations = false;
export let didInitContextData = false;
export let didInitLiveChat = false;
export let currentTrialId;
export let didInitRegion = false;

export async function init(config = {}) {
    const { features, locales, account, mtm, gtm, breadcrumbs, country, cart, localizations, context, chat, trials, region, experiment } = config;

    // Runs first, while the page is still in <head>, so Amplitude can apply a variant before first paint.
    initializeExperiment(Boolean(mtm) && experiment !== false);

    createHrefListener(newHref => {
        // Check if the new URL does not include "/checkout"
        const isNotOnCheckoutPage = !newHref.includes('/checkout');
        const [_, __, resetCheckoutLocalData] = useLocalStorage(CHECKOUT_LOCAL_DATA_KEY);

        // If the user navigates away from the checkout page, reset checkout local data to prevent unnecessary persistence.
        if (isNotOnCheckoutPage) {
            resetCheckoutLocalData();
        }
    });

    if (locales) {
        initializeLocales(locales);
        didInitLocales = true;
    }

    // If account === true, load the user with default params
    if (account === undefined || account === true) {
        initializeAccount().then(() => {
            // Once the account module is fully initialized, initialize the shopping cart (cart requires login info).
            initializeCartWithDependencies(cart, country, features);
        });
        didInitAccount = true;
    }
    // If account is configured, load the user with the passed in configuration. Make sure it's
    // not an array because that will break things!!
    else if (typeof account === 'object' && !Array.isArray(account)) {
        initializeAccount({ ...account }).then(() => {
            // Once the account module is fully initialized, initialize the shopping cart (cart requires login info).
            initializeCartWithDependencies(cart, country, features);

            if (trials) {
                currentTrialId = trials.trialId;
            }
        });
        didInitAccount = true;
    }
    // Otherwise it's a bad input and warn the developer that they are naughty
    else {
        logger.warn('Account not initialized!');
    }

    /**
     * If mtm (Matomo Tag Manager) is enabled we are going to use that configuration for tracking analytics
     * If both mtm and gtm configuration objects are defined we are going to use the mtm config
     */
    if (mtm) {
        initializeMTM({ ...mtm, sessionRequested: didInitAccount });
        didInitMTM = true;
    } else if (gtm) {
        initializeGtm({ ...gtm });
        didInitGTM = true;
    }

    if (breadcrumbs) {
        initializeBreadcrumbs({ ...breadcrumbs });
        didInitBreadcrumbs = true;
    }

    // Set up a global MutationObserver to decorate resource/webinar/trial links with
    // breadcrumb context (?bc=...) as they appear in the DOM. Running this here in init()
    // ensures the observer is active before any lazily-hydrated components render, avoiding
    // the race condition where links could be missed (XDDD-3373).
    const bcParam = getDecodedQuerystringValue('bc');
    if (bcParam) {
        try {
            const bcData = JSON.parse(bcParam);
            observeAndDecorateResourceLinks(bcData);
        } catch (e) {
            logger.warn('Could not parse bc query parameter for resource link decoration', e);
        }
    }

    // Initialize the country selector in the case where the cart isn't initialized
    if (country && !cart) {
        initializeCountry({ ...country }).then(() => {
            didInitCountry = true;
        });
    }

    // Always initialize localizations with build environment, allow explicit config to override
    initializeLocalizations({
        env: process.env.UNIVERSAL_COMPONENTS_ENV || 'prod',
        ...localizations,
    });
    didInitLocalizations = true;

    if (context) {
        initializeContextData({ env: process.env.UNIVERSAL_COMPONENTS_ENV || 'prod', ...context });
        didInitContextData = true;
    }

    if (chat) {
        initializeChat({ ...chat });
        didInitLiveChat = true;
    }

    if (region === undefined || region === true) {
        initializeRegion({ env: process.env.UNIVERSAL_COMPONENTS_ENV || 'prod' });
        didInitRegion = true;
    } else if (typeof region === 'object' && !Array.isArray(region)) {
        initializeRegion({ ...region });
        didInitRegion = true;
    } else {
        logger.warn('Region not initialized!');
    }

    // Must stay the last statement: INIT only fires once every synchronous step above has run without
    // throwing. The async initializers (account, region, ...) are started but not awaited, so this means
    // "init was called", not "the session or region has resolved" (XDDD-4764).
    didInit = true;
    emit(events.INIT);
}

/**
 * Initialize cart dependencies after account initialization. The country module is required to be fully initialized
 * prior to initializing the cart due to the fact that we need the users geolocation to create a shopping cart.
 *
 * @param {*} cart configuration passed via the init call on the disw-utils.
 * @param {*} country configuration passed via the init call on the disw-utils.
 * @param {*} features configuration passed via the init call on the disw-utils.
 */
function initializeCartWithDependencies(cart, country, features) {
    if (!cart) {
        return initializeFeaturesWithConfig(features);
    }

    // The cart requires the country be fully initialized prior to initializing the cart.
    return initializeCountry({ ...country })
        .then(() => {
            didInitCountry = true;
            return initializeFeaturesWithConfig(features);
        })
        .then(() => initializeCart({ ...cart }))
        .then(() => {
            didInitCart = true;
        });
}

function initializeFeaturesWithConfig(features) {
    if (!features) {
        return Promise.resolve();
    }

    return initializeFeatures(features).then(() => {
        didInitFeatures = true;
    });
}

export function getDidInit() {
    return didInit;
}

/**
 * Runs the callback once disw.init() has completed. If init has already run, the event manager replays
 * the emission, so the callback runs immediately. This lets components that mount before or after
 * disw.init() wait for it the same way (XDDD-4764).
 *
 * @param {Function} callback called with no arguments once init has run.
 * @returns {Function} unsubscribe function.
 */
export function onInit(callback) {
    return subscribe(events.INIT, callback);
}

export function getDidInitLocales() {
    return didInitLocales;
}

export function getDidInitAccount() {
    return didInitAccount;
}

export function getdidInitGTM() {
    return didInitGTM;
}

export function getdidInitMTM() {
    return didInitMTM;
}

export function getDidInitBreadcrumbs() {
    return didInitBreadcrumbs;
}

export function getDidInitLocalizations() {
    return didInitLocalizations;
}

export function getDidInitCountrys() {
    return didInitCountry;
}

export function getDidInitCart() {
    return didInitCart;
}

export function getDidInitContextData() {
    return didInitContextData;
}

export function getDidInitLiveChat() {
    return didInitLiveChat;
}

export function getCurrentTrialId() {
    return currentTrialId;
}

export function getDidInitRegion() {
    return didInitRegion;
}

export const account = { ...accountModule };
export const locales = { ...localesModule };
export const video = { ...videoModule };
export const breadcrumbs = { ...breadcrumbModule };
export const context = { ...contextModule };
export const localizationTokens = { ...localizationTokenModule };
export const country = { ...countryModule };
export const region = { ...regionModule };
export const cart = { ...cartModule };
export const cartAnalytics = { ...cartAnalyticsModule };
export const relatedResources = { ...relatedResourcesModule };
export const numericStepper = { ...numericStepperModule };
export const dropDown = { ...dropDownModule };
export const addedToCartModal = { ...addedToCartModule };
export const dynamicKitSlideout = { ...dynamicKitModule };
export const baseKitSlideout = { ...baseKitModule };
export const subscriptionTermsConflictModal = { ...subscriptionTermsConflictModule };
export const volumeBasedPricingModel = { ...volumeBasedPricingModelModule };
export const chat = { ...chatModule };
export const taxonomy = { ...taxonomyModule };
export const imgix = { ...imgixModule };
export const features = { ...featuresModule };
export const NavigationService = { ...NavigationServiceModule };
export const trialService = { ...trialServiceModule };
export const experiment = { ...experimentModule };
